/**
 * ShareModal - Modal for sharing rankings/achievements
 *
 * Features:
 * - Preview of share card
 * - Multiple share options (copy link, Instagram, WhatsApp, download)
 * - Card type selection
 * - Share link generation
 */
import { useState, useEffect, useRef } from 'react'
import { X, Link2, Download, Check, Share2, Instagram, MessageCircle, Loader2 } from 'lucide-react'
import shareService from '../services/shareService'
import toast from 'react-hot-toast'
import { createLogger } from '../utils/logger'

const logger = createLogger('ShareModal')

const ShareModal = ({ isOpen, onClose, rankingData, strategyType }) => {
  const [cardType, setCardType] = useState('ranking_summary')
  const [shareLink, setShareLink] = useState(null)
  const [rankingsLink, setRankingsLink] = useState(null)
  const [loading, setLoading] = useState(false)
  const [copied, setCopied] = useState(false)
  const [copiedRankings, setCopiedRankings] = useState(false)
  const [generatingImage, setGeneratingImage] = useState(false)
  const canvasRef = useRef(null)

  // Strategy display info
  const strategyInfo = {
    SAT_MATH: { name: 'SAT Math', icon: '🧮', color: '#3b82f6' },
    SAT_MATH_MINI: { name: 'SAT Math Mini', icon: '⚡', color: '#f59e0b' },
    SAT_READING_WRITING: { name: 'SAT Reading & Writing', icon: '📖', color: '#10b981' },
    SAT_READING_WRITING_MINI: { name: 'SAT R&W Mini', icon: '✏️', color: '#8b5cf6' }
  }

  const strategy = strategyInfo[strategyType] || strategyInfo.SAT_MATH

  // Card type options
  const cardTypes = [
    { id: 'ranking_summary', name: 'Ranking Summary', description: 'Your overall ranking' },
    { id: 'challenge', name: 'Best Challenge', description: 'Your highest score' },
    { id: 'level_up', name: 'Level Achievement', description: 'Your current level' }
  ]

  // Generate share link when modal opens or card type changes
  useEffect(() => {
    if (isOpen && rankingData) {
      generateShareLink()
    }
  }, [isOpen, cardType, rankingData])

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShareLink(null)
      setCopied(false)
    }
  }, [isOpen])

  const generateShareLink = async () => {
    try {
      setLoading(true)
      const result = await shareService.createShareCard({
        cardType,
        strategyType,
        data: {
          publicName: rankingData.publicName,
          rank: rankingData.rank,
          totalParticipants: rankingData.totalParticipants,
          percentile: rankingData.percentile,
          score: rankingData.score || rankingData.primaryScore,
          level: rankingData.level,
          timeSpent: rankingData.timeSpent
        }
      })
      setShareLink(result.shareUrl)

      // Generate direct rankings link that highlights this user
      if (rankingData.publicName && strategyType) {
        const baseUrl = window.location.origin
        const params = new URLSearchParams()
        params.append('highlight', rankingData.publicName)
        params.append('strategy', strategyType)
        setRankingsLink(`${baseUrl}/leaderboard?${params.toString()}`)
      }
    } catch (error) {
      logger.error('Error generating share link:', error)
      toast.error('Failed to generate share link')
    } finally {
      setLoading(false)
    }
  }

  const handleCopyLink = async () => {
    if (!shareLink) return

    try {
      await navigator.clipboard.writeText(shareLink)
      setCopied(true)
      toast.success('Link copied to clipboard!')
      setTimeout(() => setCopied(false), 3000)
    } catch (error) {
      logger.error('Error copying link:', error)
      toast.error('Failed to copy link')
    }
  }

  const handleCopyRankingsLink = async () => {
    if (!rankingsLink) return

    try {
      await navigator.clipboard.writeText(rankingsLink)
      setCopiedRankings(true)
      toast.success('Rankings link copied!')
      setTimeout(() => setCopiedRankings(false), 3000)
    } catch (error) {
      logger.error('Error copying rankings link:', error)
      toast.error('Failed to copy link')
    }
  }

  const handleShareInstagram = () => {
    // Instagram doesn't support direct URL sharing, so we generate an image
    handleDownloadImage()
    toast.success('Image downloaded! Share it on Instagram Stories')
  }

  const handleShareWhatsApp = () => {
    if (!shareLink) return

    const text = `Check out my ${strategy.name} ranking! ${strategy.icon}`
    const url = `https://wa.me/?text=${encodeURIComponent(text + '\n' + shareLink)}`
    window.open(url, '_blank')
  }

  const handleDownloadImage = async () => {
    if (!rankingData) return

    try {
      setGeneratingImage(true)

      // Check for canvas support
      const canvas = document.createElement('canvas')
      if (!canvas.getContext) {
        toast.error('Your browser does not support image generation. Please use a modern browser.')
        return
      }

      const ctx = canvas.getContext('2d')
      if (!ctx) {
        toast.error('Could not initialize canvas. Please try again or use a different browser.')
        return
      }

      // Set canvas size (Instagram Story: 1080x1920, but we'll use a square 1080x1080)
      canvas.width = 1080
      canvas.height = 1080

      // Background gradient
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height)
      gradient.addColorStop(0, strategy.color)
      gradient.addColorStop(1, '#7c3aed')
      ctx.fillStyle = gradient
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      // White card background
      ctx.fillStyle = 'rgba(255, 255, 255, 0.95)'
      roundRect(ctx, 90, 200, 900, 680, 40)
      ctx.fill()

      // Strategy icon and name (note: emoji rendering varies by browser/OS)
      ctx.font = 'bold 48px system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
      ctx.fillStyle = '#1f2937'
      ctx.textAlign = 'center'
      ctx.fillText(`${strategy.icon} ${strategy.name}`, 540, 300)

      // User name
      ctx.font = 'bold 72px system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
      ctx.fillStyle = '#111827'
      ctx.fillText(rankingData.publicName || 'Player', 540, 420)

      // Rank
      ctx.font = 'bold 120px system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
      ctx.fillStyle = strategy.color
      ctx.fillText(`#${rankingData.rank || '?'}`, 540, 580)

      // Participants
      ctx.font = '36px system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
      ctx.fillStyle = '#6b7280'
      ctx.fillText(
        `out of ${(rankingData.totalParticipants || 0).toLocaleString()} players`,
        540,
        660
      )

      // Percentile
      ctx.font = 'bold 42px system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
      ctx.fillStyle = '#10b981'
      ctx.fillText(`Top ${Math.floor(100 - (rankingData.percentile || 0))}%`, 540, 750)

      // Branding section at bottom
      // Draw a subtle separator line
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)'
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(340, 920)
      ctx.lineTo(740, 920)
      ctx.stroke()

      // Deaveris branding
      ctx.font = 'bold 36px system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
      ctx.fillStyle = 'rgba(255, 255, 255, 0.95)'
      ctx.fillText('Deaveris', 540, 965)

      // Tagline
      ctx.font = '22px system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)'
      ctx.fillText('Adaptive SAT Prep', 540, 1000)

      // Website URL
      ctx.font = '18px system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
      ctx.fillStyle = 'rgba(255, 255, 255, 0.7)'
      ctx.fillText('deaveris.com', 540, 1035)

      // Generate data URL with error handling
      let dataUrl
      try {
        dataUrl = canvas.toDataURL('image/png')
      } catch (securityError) {
        // This can happen in some browsers due to tainted canvas or security restrictions
        logger.error('Canvas security error:', securityError)
        toast.error('Could not generate image due to browser security restrictions.')
        return
      }

      if (!dataUrl || dataUrl === 'data:,') {
        toast.error('Failed to generate image. Please try again.')
        return
      }

      // Download with fallback for different browsers
      const link = document.createElement('a')
      link.download = `ranking-${rankingData.publicName || 'share'}.png`
      link.href = dataUrl

      // Use different download methods for better browser compatibility
      if (typeof link.download !== 'undefined') {
        // Modern browsers support the download attribute
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } else {
        // Fallback for Safari and older browsers: open in new tab
        window.open(dataUrl, '_blank')
        toast.success('Image opened in new tab. Right-click to save.')
        return
      }

      toast.success('Image downloaded!')
    } catch (error) {
      logger.error('Error generating image:', error)
      // Provide more specific error messages based on error type
      if (error.name === 'SecurityError') {
        toast.error('Browser security prevented image generation. Please try a different browser.')
      } else if (error.name === 'QuotaExceededError') {
        toast.error('Not enough memory to generate image. Please close other tabs and try again.')
      } else {
        toast.error('Failed to generate image. Please try again.')
      }
    } finally {
      setGeneratingImage(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Share2 className="w-5 h-5" />
              Share Your Ranking
            </h2>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4">
            {/* Card Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Card Type
              </label>
              <div className="grid grid-cols-3 gap-2">
                {cardTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => setCardType(type.id)}
                    className={`p-3 rounded-lg border-2 text-center transition-all ${
                      cardType === type.id
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {type.name}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Preview Card */}
            <div className="bg-gradient-to-br from-primary-500 to-purple-600 rounded-xl p-6 text-white text-center">
              <div className="text-3xl mb-2">{strategy.icon}</div>
              <div className="text-sm opacity-80 mb-1">{strategy.name}</div>
              <div className="text-xl font-bold mb-2">{rankingData?.publicName || 'Your Name'}</div>
              <div className="text-4xl font-bold mb-1">#{rankingData?.rank || '?'}</div>
              <div className="text-sm opacity-80">
                out of {(rankingData?.totalParticipants || 0).toLocaleString()} players
              </div>
              {rankingData?.percentile && (
                <div className="mt-2 text-green-300 font-medium">
                  Top {Math.floor(100 - rankingData.percentile)}%
                </div>
              )}
            </div>

            {/* Share Link */}
            {loading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                <span className="ml-2 text-gray-500">Generating link...</span>
              </div>
            ) : (
              shareLink && (
                <div className="space-y-3">
                  {/* Share Card Link */}
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                      Share Card Link
                    </label>
                    <div className="flex items-center gap-2 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
                      <input
                        type="text"
                        value={shareLink}
                        readOnly
                        className="flex-1 bg-transparent text-sm text-gray-700 dark:text-gray-300 outline-none truncate"
                      />
                      <button
                        onClick={handleCopyLink}
                        className={`p-2 rounded-lg transition-colors ${
                          copied
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500'
                        }`}
                      >
                        {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Rankings Page Link */}
                  {rankingsLink && (
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                        Leaderboard Link (shows your position)
                      </label>
                      <div className="flex items-center gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                        <input
                          type="text"
                          value={rankingsLink}
                          readOnly
                          className="flex-1 bg-transparent text-sm text-gray-700 dark:text-gray-300 outline-none truncate"
                        />
                        <button
                          onClick={handleCopyRankingsLink}
                          className={`p-2 rounded-lg transition-colors ${
                            copiedRankings
                              ? 'bg-green-500 text-white'
                              : 'bg-yellow-200 dark:bg-yellow-800 hover:bg-yellow-300 dark:hover:bg-yellow-700'
                          }`}
                        >
                          {copiedRankings ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <Link2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Share this to show your ranking in context with other players
                      </p>
                    </div>
                  )}
                </div>
              )
            )}

            {/* Share Buttons */}
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={handleCopyLink}
                disabled={!shareLink || loading}
                className="flex flex-col items-center gap-2 p-4 rounded-xl bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                <Link2 className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                <span className="text-xs text-gray-700 dark:text-gray-300">Copy Link</span>
              </button>

              <button
                onClick={handleShareInstagram}
                disabled={!rankingData}
                className="flex flex-col items-center gap-2 p-4 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 transition-colors disabled:opacity-50"
              >
                <Instagram className="w-6 h-6 text-white" />
                <span className="text-xs text-white">Instagram</span>
              </button>

              <button
                onClick={handleShareWhatsApp}
                disabled={!shareLink || loading}
                className="flex flex-col items-center gap-2 p-4 rounded-xl bg-green-500 hover:bg-green-600 transition-colors disabled:opacity-50"
              >
                <MessageCircle className="w-6 h-6 text-white" />
                <span className="text-xs text-white">WhatsApp</span>
              </button>
            </div>

            {/* Download Button */}
            <button
              onClick={handleDownloadImage}
              disabled={!rankingData || generatingImage}
              className="w-full flex items-center justify-center gap-2 p-3 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-colors disabled:opacity-50"
            >
              {generatingImage ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Download className="w-5 h-5" />
              )}
              <span className="font-medium">Download Image</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Helper: Draw rounded rectangle
 */
const roundRect = (ctx, x, y, width, height, radius) => {
  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.lineTo(x + width - radius, y)
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius)
  ctx.lineTo(x + width, y + height - radius)
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height)
  ctx.lineTo(x + radius, y + height)
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius)
  ctx.lineTo(x, y + radius)
  ctx.quadraticCurveTo(x, y, x + radius, y)
  ctx.closePath()
}

export default ShareModal
